import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Users, Crown, Heart, Coffee, HelpCircle, XCircle, Target, Star } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const MarketingFinalInterviewSection = ({ onBack, onNext, nextSectionTitle, hasNext, onComplete, isCompleted }) => {

  const startupAssumptions = [
    "You're smart",
    "You can do the job"
  ];

  const finalQuestions = [
    '"Do I want to work with this person every day?"',
    '"Will they fit into our team\'s vibe and way of working?"',
    '"Are they reflective, self-aware and easy to collaborate with?"'
  ];

  const interviewTypes = [
    {
      icon: Users,
      title: "Line manager interview",
      duration: "~40 mins with the person you'd report to",
      focus: "Focuses on working style, self-awareness and day-to-day team fit",
      questions: [
        '"What could you have done better in the last round?"',
        '"How do you like to be managed?"'
      ],
      type: "success"
    },
    {
      icon: Crown,
      title: "Founder interview",
      duration: "Tests alignment with company mission and long-term vision",
      focus: "May focus on ambition, storytelling and \"why us?\"",
      questions: [
        '"Why join us over a bigger team or agency?"',
        '"What\'s your biggest creative or campaign win?"',
        '"What\'s something you\'d love to build or launch here?"'
      ],
      type: "warning"
    },
    {
      icon: Heart,
      title: "Culture / values interview",
      duration: "Typically with a senior team member",
      focus: "Tests alignment with the startup's values and ways of working. You should scour the startup's video pitch, website & JD for any references to values or culture and think of 2-3 examples where you've shown these traits",
      questions: [
        '"What motivates you?"',
        '"What do you look for in a team\'s culture?"'
      ],
      type: "tip"
    },
    {
      icon: Coffee,
      title: "Team fit interview",
      duration: "More informal – could be coffee, lunch, or casual Zooms with teammates",
      focus: "The goal is simple: are you easy to get along with?",
      questions: [
        '"What do you like doing outside of work?"',
        '"What does a typical Saturday look like?"',
        '"What excites you most about joining the team?"'
      ],
      type: "info"
    }
  ];

  const goodQuestionPrinciples = [
    "Show you've thought about how you'd add value",
    "Reveal insight into startup realities (e.g. ambiguity, prioritisation, team dynamics)",
    "Demonstrate genuine curiosity and commercial awareness"
  ];

  const strongQuestions = [
    "If I joined next week, what would you expect me to take ownership of in month one?",
    "What traits have made people succeed or struggle in this role before?",
    "What's the biggest unknown the company is trying to figure out right now?",
    "If I asked the rest of the team what it's like to work here, what do you think they'd say across their different roles?"
  ];

  const avoidQuestions = [
    'Overused questions like "what\'s the culture like?" or "where do you see the company in 5 years?"',
    "Anything you can easily find on the website",
    "Questions about salary, hours or flexibility – save those for once an offer (hopefully!) lands"
  ];

  const checklist = [
    "Prepared at least 3 thoughtful, well-researched questions",
    "Considered how to demonstrate a growth mindset – e.g. what you could have done better in the previous rounds",
    "Look at the startup's video pitch, website & JD for any references to values or culture and think of 2-3 examples where you've shown these traits",
    "Know your 2-3 top impact stories, with clear, evidence-backed examples of your best work",
    'Refresh your response to "why this startup?" – showing you understand the product and the market',
    "Be prepared to bring good energy / vibes to show you're an excellent team fit"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Final interviews: sealing the deal
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Navigate culture fit and self-awareness questions to close the deal.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="🎯 What final interviews are really about">
            <div className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-blue-50 rounded-2xl p-8 border border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-6 text-lg">By this stage, the startup probably already thinks:</h4>
                  <div className="space-y-4">
                    {startupAssumptions.map((assumption, i) => (
                      <div key={i} className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-xl flex items-center justify-center mt-1">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-blue-800 leading-relaxed">{assumption}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                  <h4 className="font-bold text-green-900 mb-6 text-lg">Now they're asking:</h4>
                  <div className="space-y-4">
                    {finalQuestions.map((question, i) => (
                      <div key={i} className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-xl flex items-center justify-center mt-1">
                          <HelpCircle className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-green-800 leading-relaxed">{question}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <InfoCard
                icon={Target}
                title="Key insight"
                description="Final rounds are more about chemistry than capability. Use this stage to show how you'd work with the team day-to-day."
                type="tip"
              />
            </div>
          </StepCard>

          <StepCard title="🏢 Types of final interviews you might face">
            <div className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                {interviewTypes.map((interview, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#02B491] rounded-xl flex items-center justify-center">
                        <interview.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{interview.title}</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">{interview.duration}</p>
                      </div>
                    </div>
                    
                    <p className="text-slate-700 leading-relaxed mb-6">{interview.focus}</p>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Common questions:</h4>
                      <div className="space-y-2">
                        {interview.questions.map((question, qIndex) => (
                          <div key={qIndex} className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-2 h-2 bg-[#02B491] rounded-full mt-2"></div>
                            <p className="text-slate-600 leading-relaxed text-sm">{question}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="❓ Asking great questions in final interviews">
            <div className="space-y-10">
              <p className="text-slate-600 text-lg leading-relaxed">
                Asking strong questions is one of the easiest ways to leave a lasting impression.
              </p>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Great questions do one (or more) of the following:</h4>
                <div className="grid gap-4">
                  {goodQuestionPrinciples.map((principle, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center mt-1">
                        <Star className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-slate-700 leading-relaxed">{principle}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-emerald-500" />
                  Strong examples:
                </h4>
                <div className="space-y-6">
                  {strongQuestions.map((question, index) => (
                    <InfoCard
                      key={index}
                      title={`Example ${index + 1}`}
                      description={`"${question}"`}
                      type="success"
                    />
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl flex items-center">
                  <XCircle className="w-6 h-6 mr-3 text-red-500" />
                  What to avoid:
                </h4>
                <div className="space-y-4">
                  {avoidQuestions.map((question, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center mt-1">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </div>
                      <p className="text-slate-700 leading-relaxed">{question}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="✅ Final interview checklist">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Before your final interview, make sure you've:
              </p>
              
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="space-y-4">
                  {checklist.map((item, index) => (
                    <label key={index} className="flex items-start space-x-4 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        className="mt-1.5 rounded border-gray-300 text-[#02B491] focus:ring-[#02B491] focus:ring-offset-0" 
                      />
                      <span className="text-slate-700 leading-relaxed group-hover:text-slate-900 transition-colors">
                        {item}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

        </div>
      </div>

        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketingFinalInterviewSection; 